import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import type {TabItems, TabKey} from '@Components/tabs';
import {Tabs} from '@Components/tabs';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {TemplateFiltersScrollButton} from '@Components/poster-editor/components/poster-add-item-bar/components/template-filters-scroll-button/template-filters-scroll-button';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import type {StockMediaTabsProps} from './stock-media-tabs.types';
import {ACTIVE_TAB_SELECTOR, STOCK_TAB_METADATA} from './stock-media-tabs.types';
import styles from './stock-media-tabs.module.scss';

export function StockMediaTabs(props: StockMediaTabsProps): ReactElement {
  const tabsListRef = useRef<HTMLUListElement | null>(null);

  // The scrollable element is the <ul> the shared Tabs component renders internally; it exposes no ref, so the
  // list is grabbed from the wrapper during commit — before the scroll button's mount effect reads the ref.
  const setTabsListRef = (element: HTMLDivElement | null): void => {
    tabsListRef.current = element?.querySelector('ul') ?? null;
  };

  useEffect(() => {
    tabsListRef.current?.querySelector(ACTIVE_TAB_SELECTOR)?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
  }, [props.selectedTab]);

  // Favorites holds the user's own picks rather than a stock catalogue, so a divider sets it apart from the tab before it.
  const getTabItemClasses = (tab: StockPanelTabs): string => {
    const dividerClass = tab === StockPanelTabs.FAVORITES ? styles.tabWithDividerBefore : '';
    return `${styles.tabItem} ${dividerClass} ${props.isMobile ? styles.tabColumn : 'flexbox flex-items-center gap-1'}`;
  };

  const getTabItems = (): TabItems => {
    return props.tabs
      .filter((tab): boolean => tab !== StockPanelTabs.TEXT_TO_IMAGE)
      .map((tab) => {
        const metadata = STOCK_TAB_METADATA[tab];
        return {
          tabKey: tab,
          text: window.i18next.t(metadata.i18nKey),
          textClassName: styles.tabLabel,
          className: getTabItemClasses(tab),
          preTitleContent: <Icon icon={metadata.icon} size={props.isMobile ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_16} />,
        };
      });
  };

  return (
    <div ref={setTabsListRef} className={styles.container}>
      <Tabs
        id={props.tabId}
        selectedTab={props.selectedTab}
        tabItems={getTabItems()}
        className={`${styles.tabsBar} gap-2`}
        containerClassName="spacing-m-t-3"
        onTabClick={(tabKey: TabKey): void => {
          props.onTabClick(tabKey);
        }}
      />
      <div className={styles.scrollButtonOverlay}>
        <TemplateFiltersScrollButton containerRef={tabsListRef} iconClassName={styles.scrollButton} />
      </div>
    </div>
  );
}
